import { ProteinResponse } from "../../api/openapi-generator";

// What is the mutation ambiguity concept?
// We provide multiple isoforms for several proteins.
// User may not be aware of the correct isoform of the mutation he enters.
// In that case, the user may tend to select the canonical isoform.
// What happens when an incorrect isoform is selected:
// 1. The wild-type residue differs between the sequence and the residue entered by the user. The mutation won't be applied, the user will select another isoform.
// 2. Neighborhood around the selected position is the same for all isoforms, so selection of any isoform will lead to the same result.
// 3. The wild-type residue at the selected position is the same for multiple isoforms, but their neighborhoods differ. User should be warned that this happened to make sure he selected the correct isoform.
// This file detects the third case and provides data to show the warning.

export interface IMatchingIsoform {
  target: ProteinResponse,
  neighbourhood: string[],  // three values: left side, the mutation position, right side
}

export interface IAmbiguousMutation {
  position: number, // 1-based mutation position
  selectedIsoform: IMatchingIsoform,
  alternativeIsoforms: IMatchingIsoform[],
}

const neighbourhoodSideLength = 3;

export function detectOneAmbiguousMutation(position: number, sequence: string, targets: ProteinResponse[], selectedTarget: ProteinResponse): IAmbiguousMutation | null {
  if (!(position && sequence && targets && targets.length && selectedTarget && targets.includes(selectedTarget))) {
    return null;
  }

  const untrimmed = targets
    // mutation has to be within the isoform sequence (length might be also equal to position as position is 1-based)
    .filter(target => target.sequence.length >= position)
    // select isoforms which have the same amino acids at the position
    .filter(target => target.sequence[position - 1] === sequence[position - 1])
    // find neighborhood
    .map(target => ({
      target,
      neighbourhood: [
        target.sequence.substring(position - 1 - neighbourhoodSideLength, position - 1),
        target.sequence[position - 1],
        target.sequence.substring(position, position + neighbourhoodSideLength),
      ]
    }));
  if (untrimmed.length === 0) {
    return null;
  }

  // trim neighborhoods so that we can easily compare them
  const leftLength =  Math.min(...untrimmed.map(({ neighbourhood }) => neighbourhood[0].length));
  const rightLength = Math.min(...untrimmed.map(({ neighbourhood }) => neighbourhood[2].length));
  const trimmed = untrimmed.map(item => {
    const n = item.neighbourhood;
    return {
      ...item,
      neighbourhood: [
        n[0].slice(-leftLength),  // using minus to get the suffix of length `leftLength`
        n[1],
        n[2].slice(0, rightLength)
      ]
    }
  });

  // select only those isoforms which have different neighborhoods than selected isoform
  const selectedIsoform = trimmed.find(i => i.target === selectedTarget);
  if (!selectedIsoform) {
    console.error("Selected isoform not found among the alternative isoforms.", position, selectedTarget);
    return null;
  }
  const alternativeIsoforms = trimmed.filter(item => (
    item.neighbourhood[0] !== selectedIsoform.neighbourhood[0] || item.neighbourhood[2] !== selectedIsoform.neighbourhood[2]
  ));
  return alternativeIsoforms.length > 0 ? { position, selectedIsoform, alternativeIsoforms } : null;
}

